import { button } from "./shared";
import { fillOriginCard, type OriginCardView } from "./origins";
import { MAX_PLAYER_NAME_BYTES } from "../../world/types";

/*
 * The card that asks the player their name (chunk U-0's split of `panels.ts`;
 * chunk U-4's third beat of the opening).
 *
 * The only text input in this client and there is not going to be a second one:
 * the figure's name is the one player-authored string the whole design lets
 * across the wall. Since the owner's ruling of 2026-08-24 it only ever stands
 * in the start menus — the name is given before the first frame is drawn and
 * the record takes it at spawn, silently.
 *
 * **It is a beat rather than a box since U-4** (the owner's call of 2026-09-01:
 * *chosen card on the left, the asking on the right*). The life the question is
 * about travels out of the carousel and sits beside it, full-lit and inert — it
 * is being asked about, not offered again — and the question, the field, the
 * line the world would coin and the two ways out run down the right, flush with
 * the field's own left edge. The *menu* naming step has no card to carry, so it
 * is the same beat with one column.
 */

/**
 * What a {@link NamePrompt} says while it is up.
 *
 * Two askings share the card and they are not the same question. The birth
 * screen's is asked of a life about to start, a moment before it does; the menu
 * naming step's is asked of a session that took no life and still needs something
 * to be called (`hud/origins.ts`). Same field, same key discipline, same two ways
 * out — different words, because the words are the only part of a first-run
 * moment a player actually reads.
 *
 * **Both of them are in the start menus, and that is the whole of the owner's
 * ruling of 2026-08-24**: *"the prompt for the name should be on the starting
 * menus"*. There is no third asking and there is nothing asked in the world at
 * all — the ruling's sharpening settles identity before spawning, so what happens
 * out there is a claim nobody is consulted about
 * (`witness/witness.ts`'s `claimTheName`).
 *
 * Prose only, and one card. The card's *behaviour* has one shape and there is not
 * going to be a second: a card that varied what enter did would be a card a
 * player has to learn twice.
 */
export interface NameAsk {
  readonly title: string;
  readonly said: string;
  readonly confirm: string;
  readonly decline: string;

  /**
   * The life this name is for, carried over from the beat that offered it.
   *
   * Drawn full-lit and with nothing to press: the carousel's own card
   * (`hud/panels/origins.ts`'s {@link fillOriginCard}), so the two beats cannot
   * drift into two drawings of one life. Absent on the menu naming step, which
   * is asked of a session rather than of a life and has nothing to show.
   */
  readonly card?: OriginCardView;

  /**
   * The family name the world would write down, and the button that draws
   * another.
   *
   * **The surname ruling made visible** (owner, 2026-08-25): the line is the
   * world's to name and the player's agency is a *reroll*, so the card shows
   * what would be coined and offers another draw. Absent on every asking where
   * nothing is coined — a life that inherits a line, the menu's plain naming
   * step, a build with no preview — because a button that renamed nothing would
   * be a promise the simulation refuses to keep.
   *
   * **It is not a field.** The typed string on this card is still the only one
   * that crosses, and the ruling's ceiling is exactly that: *"the sim owns
   * surnames, permanently — one typed string stays the ceiling"*. What the
   * button submits is a press count.
   */
  readonly coined?: CoinedName;
}

/** What a coined family name looks like on the name card. See {@link NameAsk}. */
export interface CoinedName {
  /**
   * The line the world would write down at this press, in its own words.
   *
   * Straight out of the simulation and never assembled here
   * (`witness/origins.ts`'s `coinedNameOf`), which is the same bargain every
   * other name on the birth screen is drawn under.
   */
  readonly line: string;

  /** What the button says. */
  readonly again: string;

  /** Called when it is pressed. The caller advances the nonce and re-asks. */
  readonly onAgain: () => void;
}

/**
 * The one card in this client that asks the player for something: a name.
 *
 * Phase 5 decision 1's client half, and the phase's cut list is what shapes it —
 * **the figure's name is the one player-authored string that crosses the wall**,
 * so this is the one input in the tree and there is not going to be a second.
 * No form chrome beyond it: a question, a field, and two ways out.
 *
 * Three properties are load-bearing and each is a rule rather than a taste:
 *
 * - **Skipping is a real answer.** An unnamed player is simply never cited, the
 *   playtest protocol's question 8 needs declining to be a real option, and the
 *   briefing a playtester is handed says *"or don't"*
 *   (`docs/playtests/prototype-2-addendum.md`). So the second button is as
 *   plain as the first, and pressing enter on an empty field takes it.
 * - **The wall normalizes, not this.** Whitespace, control characters and the
 *   ends are the simulation's business, because the replay format has to agree
 *   with exactly one normalization and two that disagree are a world that
 *   cannot be replayed (`world/types.ts`, {@link WorldSource.claim}). What this
 *   does is a *courtesy* cut so the player can see what will survive, at the
 *   same character boundary the wall cuts on.
 * - **The keyboard belongs to the field while it is open.** `input/controls.ts`
 *   listens on `document`, so a `w` typed into a name would walk; the key
 *   events are stopped at the field instead of the controls being taught about
 *   panels. **A digit typed into a name is part of the name** — the lab's rule
 *   (`ui-lab/surfaces/opening.html`), and the reason it is worth stating twice
 *   is that the hotbar takes the digits in this same wave (chunk U-5). Two
 *   guards say it: this one stops the event at the field, and every key handler
 *   the opening's own flow binds returns on an `HTMLInputElement` target
 *   (`hud/opening.ts`, `hud/origins.ts`).
 *
 * **Since the owner's ruling of 2026-08-24 this card only ever stands in the
 * start menus.** Phase 6 gave it a second asking and not a second card
 * ({@link NameAsk}) — the birth screen's — and the ruling moved the third one
 * there too: a session that takes no life is asked at the menu rather than a
 * minute into play (`hud/origins.ts`). Nothing types a name while the world is
 * being drawn any more, so the third bullet above is now a rule about a keyboard
 * `Controls` has not been given yet rather than one it is fighting over — kept
 * word for word, because what makes it safe is a fact about `main.ts`'s boot
 * order and not about this class.
 *
 * Nothing happens out in the world at all: the ruling's sharpening deleted the
 * confirmation that used to stand there, and the name a session gave here is put
 * into the record at spawn without another word (`witness/witness.ts`'s
 * `claimTheName`).
 */
export class NamePrompt {
  /**
   * The beat: a full-frame layer with a two-column row centred on it.
   *
   * **Not a `hud__panel`**, on the reading window's reasoning (chunk U-2): the
   * chassis is a box in a corner with a plate, and this is one beat of a
   * sequence read against a veil somebody else is holding up
   * (`hud/panels/origins.ts`'s `veil`). `hud__panel--name` stays, because it is
   * what three checks and two drivers select by (plan D2), and
   * `ui/opening.css` states the `[hidden]` rule the chassis used to lend it.
   */
  private readonly element: HTMLElement;

  /** Where the chosen life is drawn, or nothing at all on the menu's asking. */
  private readonly lifeElement: HTMLElement;

  private readonly field: HTMLInputElement;
  private readonly roomElement: HTMLElement;
  private readonly titleElement: HTMLElement;
  private readonly saidElement: HTMLElement;
  private readonly confirmButton: HTMLButtonElement;
  private readonly declineButton: HTMLButtonElement;

  /** The coined line, and the button that draws another. Hidden where nothing is coined. */
  private readonly coinedElement: HTMLElement;
  private readonly coinedLine: HTMLElement;
  private readonly coinButton: HTMLButtonElement;

  /** What pressing the button does this asking, or `null` while nothing is coined. */
  private coinAgain: (() => void) | null = null;

  /** Called with the typed name, or with `null` when the player declined. */
  private answer: ((name: string | null) => void) | null = null;

  constructor(root: HTMLElement) {
    this.element = document.createElement("div");
    this.element.className = "hud__panel--name hud__opening";
    this.element.hidden = true;

    // The card the question is about. It wears the carousel's own centred
    // dressing, because that is what it is: the life that was just chosen,
    // still lit. `aria-hidden` because everything on it has just been read on
    // the beat before and a screen reader should be taken to the question.
    this.lifeElement = document.createElement("aside");
    this.lifeElement.className = "hud__origins-card hud__origins-card--at hud__name-life";
    this.lifeElement.setAttribute("aria-hidden", "true");
    this.lifeElement.hidden = true;

    // `hud__panel-title` is kept on the question — it is what
    // `.hud__panel--name .hud__panel-title` selects in two checks and two
    // drivers, and the identity a name is drawn under (plan D2).
    this.titleElement = document.createElement("div");
    this.titleElement.className = "hud__panel-title hud__name-title";

    this.saidElement = document.createElement("p");
    this.saidElement.className = "hud__name-said";

    this.field = document.createElement("input");
    this.field.type = "text";
    // The kit's field beside the client's own name: it is the one text input in
    // the game and the kit graduated it from this very card (chunk U-0).
    this.field.className = "hud__name-field name-field";
    this.field.autocomplete = "off";
    this.field.spellcheck = false;
    this.field.setAttribute("aria-label", "your name");
    this.field.addEventListener("input", () => {
      this.trim();
    });
    // Every key event stops here. `.hud` is `pointer-events: none` and this
    // turns pointer events back on for itself, which is the file's other rule.
    this.field.addEventListener("keyup", (event) => {
      event.stopPropagation();
    });
    this.field.addEventListener("keydown", (event) => {
      event.stopPropagation();

      if (event.key === "Enter") {
        event.preventDefault();
        this.settle(this.field.value.trim() === "" ? null : this.field.value);
      }

      if (event.key === "Escape") {
        event.preventDefault();
        this.settle(null);
      }
    });

    // How much room is left, inside the field's own box — which is why the kit's
    // `.name-field` carries a deep right padding (`ui/kit.css`).
    this.roomElement = document.createElement("div");
    this.roomElement.className = "hud__name-room";

    const fieldWrap = document.createElement("div");
    fieldWrap.className = "hud__name-field-wrap";
    fieldWrap.append(this.field, this.roomElement);

    this.confirmButton = button("", () => {
      this.settle(this.field.value.trim() === "" ? null : this.field.value);
    });
    // One gold edge in a bar, and it is the way on (the kit's own rule).
    this.confirmButton.classList.add("panel-button", "panel-button--lead");
    this.declineButton = button("", () => {
      this.settle(null);
    });
    this.declineButton.classList.add("panel-button");

    const buttons = document.createElement("div");
    buttons.className = "hud__name-buttons";
    buttons.append(this.confirmButton, this.declineButton);

    this.coinedLine = document.createElement("div");
    this.coinedLine.className = "hud__name-coined";

    this.coinButton = button("", () => {
      this.coinAgain?.();
    });
    this.coinButton.classList.add("panel-button");

    this.coinedElement = document.createElement("div");
    this.coinedElement.className = "hud__name-coin";
    this.coinedElement.hidden = true;
    this.coinedElement.append(this.coinedLine, this.coinButton);

    const ask = document.createElement("div");
    ask.className = "hud__name-ask";
    ask.append(
      this.titleElement,
      this.saidElement,
      fieldWrap,
      // Between the field and the two ways out, because that is the order the
      // question reads in: what you are called, then what the world will call
      // your line, then the answer.
      this.coinedElement,
      buttons,
    );

    const row = document.createElement("div");
    row.className = "hud__opening-row";
    row.append(this.lifeElement, ask);

    this.element.append(row);
    root.append(this.element);
  }

  get open(): boolean {
    return !this.element.hidden;
  }

  /**
   * Shows the card and takes the focus.
   *
   * `answer` is called exactly once, with what the player typed or with `null`.
   * Asking again while it is open replaces the callback rather than stacking
   * one behind it — a second ask is a bug in the caller and answering the first
   * one twice would be worse than losing it.
   *
   * `said` is which of the card's askings this is, and it has no default: both
   * of them are the start menus' ({@link NameAsk}) and a card that would put up
   * *some* wording if a caller forgot to say which is a card that can be shown at
   * the wrong moment without anybody noticing. It used to default to Phase 5's
   * claim wording, which is the one asking that no longer exists.
   */
  ask(answer: (name: string | null) => void, said: NameAsk): void {
    this.answer = answer;
    this.titleElement.textContent = said.title;
    this.saidElement.textContent = said.said;
    this.confirmButton.textContent = said.confirm;
    this.declineButton.textContent = said.decline;
    this.showLife(said.card ?? null);
    this.showCoined(said.coined ?? null);
    this.field.value = "";
    this.room();
    this.element.hidden = false;
    this.field.focus();
  }

  /**
   * Re-draws the coined line without disturbing what is typed.
   *
   * What *coin another* does: the caller advances the nonce, asks the simulation
   * again and hands the new line back here. The field is untouched on purpose —
   * a player halfway through typing a given name has not asked for it to be
   * cleared because they pressed a button about a family one.
   */
  recoin(coined: CoinedName): void {
    this.showCoined(coined);
  }

  /** Takes it away without answering. Only the caller giving up calls it. */
  hide(): void {
    this.answer = null;
    this.coinAgain = null;
    this.element.hidden = true;
  }

  /** Carries the chosen life into the beat, or draws one column instead of two. */
  private showLife(card: OriginCardView | null): void {
    this.lifeElement.hidden = card === null;

    if (card === null) {
      this.lifeElement.replaceChildren();

      return;
    }

    fillOriginCard(this.lifeElement, card);
  }

  /** Puts the coined line and its button up, or takes both away. */
  private showCoined(coined: CoinedName | null): void {
    this.coinAgain = coined?.onAgain ?? null;
    this.coinedElement.hidden = coined === null;

    if (coined === null) {
      return;
    }

    this.coinedLine.textContent = coined.line;
    this.coinButton.textContent = coined.again;
  }

  /** Answers once, and closes. */
  private settle(name: string | null): void {
    const answer = this.answer;

    this.answer = null;
    this.coinAgain = null;
    this.element.hidden = true;
    answer?.(name);
  }

  /**
   * Cuts the field to what the wall will keep, at a character boundary.
   *
   * The cap is in **bytes** because that is what the wall promises, and a client
   * counting them counts UTF-8 rather than `length` — the two differ for exactly
   * the names where it matters ({@link MAX_PLAYER_NAME_BYTES}). Walked by code
   * point, which is the boundary the simulation cuts on, so the field never
   * shows half a character.
   *
   * A courtesy rather than a validation: the simulation cuts the same string the
   * same way whatever happens here.
   */
  private trim(): void {
    const encoder = new TextEncoder();
    let used = 0;
    let kept = "";

    for (const character of this.field.value) {
      const size = encoder.encode(character).length;

      if (used + size > MAX_PLAYER_NAME_BYTES) {
        break;
      }

      used += size;
      kept += character;
    }

    if (kept !== this.field.value) {
      this.field.value = kept;
    }

    this.room();
  }

  /**
   * Says how much room is left, without naming the unit.
   *
   * "Bytes" is a word a player would have to look up, which is the standing rule
   * for every string in this client (`witness/witness.ts`'s labels) — and the
   * number is never transcribed either way, it is read off the boundary.
   */
  private room(): void {
    const used = new TextEncoder().encode(this.field.value).length;
    const left = MAX_PLAYER_NAME_BYTES - used;

    // Nothing at all on an empty field: "room for 32 more" over a blank box is a
    // number about nothing, and the cap is only worth saying once somebody is
    // near it. The card is a question, not a form.
    this.roomElement.textContent =
      used === 0 ? "" : left <= 0 ? "that is as long as a name gets here" : `room for ${left} more`;
  }
}
